import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container, Typography, Box, Grid, CardMedia, Button,
  ThemeProvider, createTheme, CssBaseline, Paper, Divider, IconButton
} from '@mui/material';
import { styled } from '@mui/system';
import Cart from "../Cart";
import CustomerReview from "./Review/CustomerReview";
import User from "../data/User";
import API_URL from '../apiConfig';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#e88ba9',
    },
    background: {
      default: '#fffafb',
    },
    text: {
      primary: '#5c434a',
      secondary: '#8c7077',
    },
  },
  typography: {
    fontFamily: '"Outfit", "Inter", sans-serif',
    h3: {
      fontFamily: '"Playfair Display", serif',
      color: '#d46a89',
      fontWeight: 800
    },
    h4: {
      fontFamily: '"Playfair Display", serif',
      color: '#d46a89',
      fontWeight: 700
    },
    h5: {
      fontFamily: '"Playfair Display", serif',
      color: '#d46a89',
      fontWeight: 600
    }
  },
});

const BakeryButton = styled(Button)({
  background: '#e88ba9',
  borderRadius: '30px',
  border: 0,
  color: 'white',
  padding: '12px 32px',
  boxShadow: '0 8px 16px rgba(232, 139, 169, 0.4)',
  fontWeight: 700,
  textTransform: 'none',
  fontSize: '1.05rem',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 20px rgba(212, 106, 137, 0.6)',
    background: '#d46a89',
  },
  '&:active': {
    transform: 'translateY(0)',
  }
});

const OutlineButton = styled(Button)({
  borderRadius: '30px',
  color: '#8c7077',
  borderColor: '#e88ba9',
  padding: '8px 24px',
  fontWeight: 600,
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    color: '#d46a89',
    backgroundColor: '#fcf4f4',
  },
});

const QtyButton = styled(IconButton)({
  backgroundColor: '#fffafb',
  border: '1px solid #fcf4f4',
  color: '#d46a89',
  '&:hover': {
    backgroundColor: '#fcf4f4',
  }
});

const Cake = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cake, setCake] = useState(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const fetchCake = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/cakes/${id}`);
        setCake(response.data);
      } catch (error) {
        console.error('Error fetching cake:', error);
      }
    };

    fetchCake();
    User.instance.fetchData().then(() => {
      setQuantity(q => q);
    });
  }, [id]);

  const handleDecrease = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleIncrease = () => {
    setQuantity(quantity + 1);
  };

  const handleAddToCart = () => {
    if (cake) {
      Cart.addCake(cake, quantity);
      alert('Sweet treat added to your cart! 🧁');
    }
  };

  if (!cake) {
    return (
      <ThemeProvider theme={defaultTheme}>
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fffafb' }}>
          <Typography variant="h5" sx={{ color: '#d46a89', fontStyle: 'italic' }}>Baking your request...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ background: '#fffafb', minHeight: '100vh', pt: '100px', pb: 8 }}>
        <CssBaseline />
        <Container maxWidth="lg">
          <Box sx={{ mb: 4 }}>
            <OutlineButton startIcon={<ArrowBackIcon />} onClick={() => navigate('/menu')}>
              Back to Menu
            </OutlineButton>
          </Box>

          <Paper elevation={0} sx={{
            borderRadius: '32px', border: '1px solid #fcf4f4', background: '#fff',
            boxShadow: '0 20px 40px rgba(0,0,0,0.03)', overflow: 'hidden'
          }}>
            <Grid container>
              {/* Image Section */}
              <Grid item xs={12} md={6}>
                <Box sx={{ height: '100%', minHeight: { xs: '300px', md: '500px' }, position: 'relative' }}>
                  <CardMedia
                    component="img"
                    image={cake.image || 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&q=80&w=800'}
                    alt={cake.name}
                    sx={{
                      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>
              </Grid>

              {/* Details Section */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: { xs: 4, md: 6 }, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center' }}>
                  <Typography variant="h3" component="h1" gutterBottom sx={{ fontSize: { xs: '2.5rem', md: '3rem' }, lineHeight: 1.1 }}>
                    {cake.name}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#e88ba9', textTransform: 'uppercase', letterSpacing: '2px', fontSize: '0.85rem', fontWeight: 700, mb: 3 }}>
                    {cake.category}
                  </Typography>

                  <Divider sx={{ borderColor: '#fcf4f4', mb: 3 }} />

                  <Typography variant="body1" paragraph sx={{ color: '#8c7077', fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
                    {cake.description}
                  </Typography>

                  <Typography variant="h4" component="p" sx={{ color: '#5c434a', mb: 4 }}>
                    {cake.price}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                    {/* Quantity Selector */}
                    <Box sx={{ display: 'flex', alignItems: 'center', background: '#fffafb', borderRadius: '30px', p: 0.5, border: '1px solid #fcf4f4' }}>
                      <QtyButton onClick={handleDecrease} size="small">
                        <RemoveIcon fontSize="small" />
                      </QtyButton>
                      <Typography sx={{ px: 3, fontWeight: 700, color: '#5c434a', width: '40px', textAlign: 'center' }}>
                        {quantity}
                      </Typography>
                      <QtyButton onClick={handleIncrease} size="small">
                        <AddIcon fontSize="small" />
                      </QtyButton>
                    </Box>

                    <BakeryButton
                      startIcon={<AddShoppingCartIcon />}
                      onClick={handleAddToCart}
                      fullWidth={false}
                      sx={{ flexGrow: { xs: 1, sm: 0 } }}
                    >
                      Add to Cart
                    </BakeryButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Reviews Section */}
          {User.instance.token && User.instance.email && (
            <Box sx={{ mt: 8 }}>
              <Typography variant="h4" sx={{ mb: 4, textAlign: 'center' }}>Sweet Reviews</Typography>
              <CustomerReview userName={User.instance.email} itemId={id} />
            </Box>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Cake;